import React, { useEffect, useState } from 'react';
import API from '../../services/api';
import EditPriceForm from '../../components/Price/EditPriceForm';
import { t } from "@lingui/macro";

const Prices = () => {
  const [prices, setPrices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'priceCategory', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [editingPrice, setEditingPrice] = useState(null);
  const pricesPerPage = 10;

  useEffect(() => {
    fetchPrices();
  }, []);

  const fetchPrices = async () => {
    try {
      const response = await API.get('/api/prices');
      setPrices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching prices:', error);
      setPrices([]); 
    }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '▲' : '▼';
    }
    return '';
  };

  const handleEditClick = (price) => {
    if (!price || !price.id) {
      console.error('Error: Price object is invalid or missing ID', price);
      return;
    }
    setEditingPrice(price);
  };

  const handleSave = async (updatedPrice) => {
    try {
      if (!updatedPrice.id) {
        throw new Error('Price ID is missing');
      }
      const response = await API.put(`/api/prices/${updatedPrice.id}`, updatedPrice);
      setPrices((prevPrices) =>
        prevPrices.map((price) => (price.id === updatedPrice.id ? response.data : price))
      );
      setEditingPrice(null); 
    } catch (error) {
      console.error('Error updating price:', error);
    }
  };

  const handleAddNew = () => {
    setEditingPrice({ priceCategory: '', type: '', price: '', currency: '', id: null }); 
  };

  const handleAdd = async (newPrice) => {
    try {
      const response = await API.post('/api/prices', newPrice);
      setPrices((prevPrices) => [...prevPrices, response.data]);
      setEditingPrice(null); 
    } catch (error) {
      console.error('Error adding price:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/api/prices/${id}`);
      setPrices((prevPrices) => prevPrices.filter((price) => price.id !== id));
      setEditingPrice(null); 
    } catch (error) {
      console.error('Error deleting price:', error);
    }
  };

  const handleCancel = () => setEditingPrice(null);

  const filteredPrices = prices
    .filter((price) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        price.priceCategory?.toLowerCase().includes(searchLower) ||
        price.type?.toLowerCase().includes(searchLower) ||
        price.price?.toString().includes(searchTerm)
      );
    })
    .sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastPrice = currentPage * pricesPerPage;
  const indexOfFirstPrice = indexOfLastPrice - pricesPerPage;
  const currentPrices = filteredPrices.slice(indexOfFirstPrice, indexOfLastPrice);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div>
      {editingPrice ? (
        <EditPriceForm
          price={editingPrice}
          onSave={editingPrice.id ? handleSave : handleAdd}
          onCancel={handleCancel}
          onDelete={handleDelete}
        />
      ) : (
        <div>
          <h2>{t`Manage Prices`}</h2>
          <p>{t`Search and manage the pricing of your services and products.`}</p>

          {/* Search and add new price */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
            <input
              type="text"
              placeholder={t`Search by price category, type, or amount...`}
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{ flex: 1 }}
            />
            <button
              onClick={handleAddNew}
              className="add-new-btn"
              style={{
                backgroundColor: 'rgb(250, 73, 15)', 
                color: 'white', 
                padding: '8px 16px', 
                border: 'none', 
                borderRadius: '4px', 
                marginLeft: '20px',
              }}
            >
              {t`Add New Price`}
            </button>
          </div>

          {/* Table */}
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => handleSort('priceCategory')} className="sortable">
                  {t`Price Category`} {getSortIcon('priceCategory')}
                </th>
                <th onClick={() => handleSort('type')} className="sortable">
                  {t`Type`} {getSortIcon('type')}
                </th>
                <th onClick={() => handleSort('price')} className="sortable">
                  {t`Price`} {getSortIcon('price')}
                </th>
                <th onClick={() => handleSort('currency')} className="sortable">
                  {t`Currency`} {getSortIcon('currency')}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentPrices.map((price, index) => (
                <tr key={price.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                  <td onClick={() => handleEditClick(price)} className="clickable">
                    {price.priceCategory}
                  </td>
                  <td>{price.type}</td>
                  <td>{price.price}</td>
                  <td>{price.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            {Array.from({ length: Math.ceil(filteredPrices.length / pricesPerPage) }, (_, index) => (
              <button
                key={index + 1}
                onClick={() => paginate(index + 1)}
                className={currentPage === index + 1 ? 'active-page' : ''}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Prices;
